import React from 'react';
import './Upload.css';

export interface WxbUploadFile {
  name: string;
  size?: number;
  status?: 'done' | 'uploading' | 'error';
  uid: string;
}

export interface WxbUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFilesSelected?: (files: File[]) => void;
  fileList?: WxbUploadFile[];
}

const FILE_STATUS_LABELS: Record<NonNullable<WxbUploadFile['status']>, string> = {
  done: '已完成',
  uploading: '上传中',
  error: '上传失败',
};

export const WxbUpload: React.FC<WxbUploadProps> = ({
  accept,
  multiple = false,
  disabled = false,
  onFilesSelected,
  fileList = [],
  children,
  className = '',
  'aria-label': ariaLabel = '选择上传文件',
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);

  const handleFiles = React.useCallback((files: FileList | null) => {
    if (!disabled && files) onFilesSelected?.(Array.from(files));
  }, [disabled, onFilesSelected]);

  const handleDrop = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    if (!disabled) handleFiles(event.dataTransfer.files);
  }, [disabled, handleFiles]);

  const openFileDialog = () => {
    if (!disabled) inputRef.current?.click();
  };

  return (
    <div
      {...props}
      className={`wxb-upload ${disabled ? 'is-disabled' : ''} ${className}`}
    >
      <div
        className={`wxb-upload-dragger ${dragOver ? 'is-dragover' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onClick={openFileDialog}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openFileDialog();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          className="wxb-upload-input"
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            handleFiles(event.target.files);
            event.currentTarget.value = '';
          }}
        />
        {children || (
          <>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--wx-fg-4)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <div className="wxb-upload-text">点击或拖拽文件到此区域</div>
            <div className="wxb-upload-hint">支持单个或批量上传</div>
          </>
        )}
      </div>
      {fileList.length > 0 && (
        <ul className="wxb-upload-list" aria-live="polite" aria-label="上传文件">
          {fileList.map((file) => {
            const status = file.status || 'done';
            return (
              <li key={file.uid} className={`wxb-upload-file wxb-upload-${status}`}>
                <span className="wxb-upload-name">{file.name}</span>
                {file.size !== undefined && (
                  <span className="wxb-upload-size">{(file.size / 1024).toFixed(1)} KB</span>
                )}
                <span className="wxb-upload-status">{FILE_STATUS_LABELS[status]}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
